import { Button, Form, Input, message, Modal, Row } from "antd";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from '@store/store.tsx';
import { initGuestSession, updateProfile } from "@store/slices/userSlice.tsx";
import { selectContactVerificationRequired, selectGuestCheckoutSetting } from '@store/slices/settingsSlice.tsx';
import { normalizeEmail, normalizePhone } from '@eshop/utils/contactNormalization.ts';
import PhoneInput from '@tools/components/PhoneInput';
import ContactVerificationModal from '@features/account/components/ContactVerificationModal';
import { useContactVerification, type ContactVerificationRequest } from '@features/account/hooks/useContactVerification';

interface ModalBasicInfoUserProps {
    isModalOpen: boolean;
    setIsModalOpen: (open: boolean) => void;
    closeAvailable?: boolean;
    // onSubmit: (values: { email?: string; phone: string }) => void;
}

const getVerificationErrorMessage = (error: unknown) => {
    if (error && typeof error === 'object' && 'code' in error && (error as { code?: unknown }).code === 'otp_delivery_failed') {
        return 'No se pudo enviar el código porque el servicio de mensajería no está disponible. Intentá nuevamente más tarde.';
    }
    if (error && typeof error === 'object' && 'message' in error) {
        const detail = (error as { message?: unknown }).message;
        if (typeof detail === 'string' && detail.trim()) return detail;
    }
    return 'No se pudo enviar el código de verificación';
};

export const ModalBasicInfoUser: React.FC<ModalBasicInfoUserProps> = ({ isModalOpen, setIsModalOpen, closeAvailable = false }) => {
    const user = useSelector((state: RootState) => state.user.profile);
    const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
    const guestCheckoutSetting = useSelector(selectGuestCheckoutSetting);
    const contactVerificationRequired = useSelector(selectContactVerificationRequired);
    const allowGuestCheckout = guestCheckoutSetting !== false;

    const dispatch = useAppDispatch();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [phone, setPhone] = useState('');
    const pendingContactsRef = useRef<ContactVerificationRequest[]>([]);
    const [form] = Form.useForm<{ email?: string; phone?: string }>();
    const {
        state: contactState,
        isModalOpen: isContactVerificationOpen,
        requestVerification,
        confirmVerification,
        cancelVerification,
    } = useContactVerification();

    const requiresEmail = !user?.email;
    const requiresPhone = !user?.phone;

    useEffect(() => {
        form.setFieldsValue({
            email: user?.email ?? '',
            phone: user?.phone ?? '',
        });
        setPhone(user?.phone ?? '');
    }, [user, form]);

    const handleConfirmVerification = useCallback(async (code: string) => {
        await confirmVerification(code);
        const [, ...rest] = pendingContactsRef.current;
        pendingContactsRef.current = rest;
        if (rest.length > 0) {
            try {
                await requestVerification(rest[0]);
            } catch (error) {
                message.error(getVerificationErrorMessage(error));
            }
            return;
        }
        cancelVerification();
    }, [cancelVerification, confirmVerification, requestVerification]);

    const handleModalSubmit = async () => {
        try {
            const values = await form.validateFields();
            const rawEmail = requiresEmail ? values.email : user?.email;
            const normalizedEmail = normalizeEmail(rawEmail) ?? '';
            const rawPhone = requiresPhone ? phone : user?.phone;
            const normalizedPhone = normalizePhone(rawPhone);
            if (!normalizedPhone) {
                form.setFields([
                    {
                        name: 'phone',
                        errors: ['Ingrese un teléfono válido con prefijo internacional'],
                    },
                ]);
                return;
            }

            setIsSubmitting(true);
            if (isAuthenticated) {
                const updates = {
                    ...(requiresEmail ? { email: normalizedEmail } : {}),
                    ...(requiresPhone ? { phone: normalizedPhone } : {}),
                };
                if (!contactVerificationRequired) {
                    await dispatch(updateProfile(updates)).unwrap();
                    setIsModalOpen(false);
                } else {
                    const contactQueue: ContactVerificationRequest[] = [];
                    if (requiresEmail && normalizedEmail) {
                        contactQueue.push({ channel: 'email', value: normalizedEmail });
                    }
                    if (requiresPhone && normalizedPhone) {
                        contactQueue.push({ channel: 'phone', value: normalizedPhone });
                    }
                    pendingContactsRef.current = contactQueue;
                    if (contactQueue.length > 0) {
                        try {
                            await requestVerification(contactQueue[0]);
                            setIsModalOpen(false);
                        } catch (error) {
                            message.error(getVerificationErrorMessage(error));
                        }
                    }
                }
            } else {
                if (!allowGuestCheckout) {
                    message.error('El checkout para invitados está deshabilitado. Iniciá sesión para continuar.');
                    setIsModalOpen(false);
                    return;
                }
                await dispatch(
                    initGuestSession({
                        email: normalizedEmail,
                        phone: normalizedPhone,
                    }),
                ).unwrap();
                message.success('Información guardada 👍');
                setIsModalOpen(false);
            }
        } catch (err) {
            if (typeof err === 'object' && err && 'errorFields' in err) {
                return;
            }
            const detail = typeof err === 'string' ? err : 'No se pudo guardar la información';
            message.error(detail);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isAuthenticated && !allowGuestCheckout) {
        return null;
    }

    return (
        <>
        <Modal
            title={!isAuthenticated ? 'Ingrese su email y teléfono' : 'Ingrese su teléfono'}
            open={isModalOpen}
            footer={null}
            onCancel={() => setIsModalOpen(false)}
            closable={closeAvailable}
            maskClosable={closeAvailable} // Evitar que se cierre al hacer clic fuera
        >
            <Form
                form={form}
                layout='vertical'
                onFinish={handleModalSubmit}
                initialValues={{
                    email: user?.email ?? '',
                    phone: user?.phone ?? '',
                }}
            >
                {requiresEmail ? (
                    <Form.Item
                        name='email'
                        label='Email'
                        rules={[
                            { required: true, message: 'El email es obligatorio' },
                            { type: 'email', message: 'Ingrese un email válido' },
                        ]}
                    >
                        <Input placeholder='Ingrese su email' autoComplete='email' />
                    </Form.Item>
                ) : null}
                {requiresPhone ? (
                    <Form.Item
                        name='phone'
                        label='Teléfono'
                        rules={[
                            { required: true, message: 'El teléfono es obligatorio' },
                        ]}
                    >
                        <PhoneInput
                            value={phone}
                            placeholder='Celular'
                            defaultCountry="AR"
                            international
                            onChange={(value) => {
                                const nextPhone = value ?? '';
                                setPhone(nextPhone);
                                form.setFieldValue('phone', nextPhone);
                            }}
                        />
                    </Form.Item>
                ) : null}
                <Row className="justify-content-end mt-3">
                    <Button size="small"
                        className="rounded-pill px-3 py-0 hover_info hover_border_0"
                        htmlType="submit"
                        loading={isSubmitting}
                    >Guardar</Button>
                </Row>
            </Form>
        </Modal>
        <ContactVerificationModal
            open={isContactVerificationOpen}
            channel={contactState.channel}
            maskedTarget={contactState.maskedTarget}
            expiresAt={contactState.expiresAt}
            status={contactState.status}
            error={contactState.error}
            remainingAttempts={contactState.remainingAttempts}
            retryAfter={contactState.retryAfter}
            onConfirm={handleConfirmVerification}
            onCancel={cancelVerification}
        />
        </>
    );
};
